import { fromEnumAsString, toEnumFromString } from '../../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../../utility/map-utils.js';
import { cloneData, isSameData } from '../../../../dto/data-model-utils.js';
import {
  SerializedData, SerializationContext, DataModelBuilder,
} from '../../../../dto/data-model.js';
import { EventTicketOrder } from '../../../event-ticket-order.js';
import { PaymentMethod } from '../../../payment-method.js';
import { AuditLogEntryType } from '../../audit-log-entry-type.js';
import { AuditLogEntry } from '../../audit-log-entry.js';

export class OfflineEventTicketOrderPaymentMethodChangedEntry extends AuditLogEntry {
  static readonly TYPE_ID: string = '0a74a6c39b1542f9afe53b7eeb53cda5';

  private constructor(
    public readonly oldPaymentMethod: PaymentMethod,
    public readonly newPaymentMethod: PaymentMethod,
    public readonly before: EventTicketOrder,
    public readonly after: EventTicketOrder,
  ) {
    super();
  }

  get type(): AuditLogEntryType {
    return AuditLogEntryType.OfflineEventTicketOrderPaymentMethodChanged;
  }

  generateQueryData(): SerializedData {
    return {
      businessRef: this.after.businessRef.encode(),
      eventId: this.after.eventId,
      orderId: this.after.uniqueId,
    };
  }

  get $typeId(): string {
    return OfflineEventTicketOrderPaymentMethodChangedEntry.TYPE_ID;
  }

  static create(args: {
    oldPaymentMethod: PaymentMethod,
    newPaymentMethod: PaymentMethod,
    before: EventTicketOrder,
    after: EventTicketOrder,
  }): OfflineEventTicketOrderPaymentMethodChangedEntry {
    return new OfflineEventTicketOrderPaymentMethodChangedEntry(
        args.oldPaymentMethod,
        args.newPaymentMethod,
        args.before,
        args.after,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineEventTicketOrderPaymentMethodChangedEntry {
    return this.create({
      oldPaymentMethod: toEnumFromString(MapUtils.getString(map, 'oldPaymentMethod'), PaymentMethod),
      newPaymentMethod: toEnumFromString(MapUtils.getString(map, 'newPaymentMethod'), PaymentMethod),
      before: context.fromData<EventTicketOrder>(MapUtils.getRecord(map, 'before')),
      after: context.fromData<EventTicketOrder>(MapUtils.getRecord(map, 'after')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      oldPaymentMethod: fromEnumAsString(this.oldPaymentMethod),
      newPaymentMethod: fromEnumAsString(this.newPaymentMethod),
      before: context.toData(this.before),
      after: context.toData(this.after),
    };
  }

  clone(): this {
    return new OfflineEventTicketOrderPaymentMethodChangedEntry(
        this.oldPaymentMethod,
        this.newPaymentMethod,
        cloneData(this.before),
        cloneData(this.after),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.oldPaymentMethod === this.oldPaymentMethod &&
        other.newPaymentMethod === this.newPaymentMethod &&
        isSameData(other.before, this.before) &&
        isSameData(other.after, this.after);
  }
}

export class OfflineEventTicketOrderPaymentMethodChangedEntryBuilder extends DataModelBuilder<OfflineEventTicketOrderPaymentMethodChangedEntry> {
  build(context: SerializationContext, map: SerializedData): OfflineEventTicketOrderPaymentMethodChangedEntry {
    return OfflineEventTicketOrderPaymentMethodChangedEntry.fromMap(context, map);
  }
}
